/**
 * MODULE: _shared/frontend-shell/src/design-system/components/Navigation/TopBar.tsx
 * PURPOSE: Top navigation bar with user profile, notifications, and search
 * 
 * DEPENDENCIES:
 *   - flowbite-react: Navbar
 *   - @heroicons/react: BellIcon, MagnifyingGlassIcon
 *   - React: ReactNode
 * 
 * PUBLIC API:
 *   - TopBar: Main component
 *   - TopBarProps: Component props interface
 * 
 * SIDE EFFECTS:
 *   - React rendering
 * 
 * AGENT SAFETY: 🟢 SAFE - Presentational component
 * FILE SIZE: ~175 / 200 lines
 * 
 * Agent Notes:
 * - Flowbite Navbar; account actions live in **`UserMenu`** (dropdown: profile, workspace, theme, shortcuts, sign out)
 * - Top-left square app icon + **Brazn ai** wordmark (`public/brazn-logo-header-icon.png`); tenantName prop reserved but not displayed
 * - RECENT: 2026-08-06 [2.2.2137] — Brand cluster `shrink-0` so Traditional↔Agentic toggle stays visible when the bar compresses; wordmark hides below `lg`.
 * - RECENT_PRIOR: 2026-08-03 [2.2.2023] — Brand/toggle vertical align: py-0 + leading-none cluster.
 * 
 * See: /README.md | /ARCHITECTURE.md | /_shared/frontend-shell/README.md
 */

import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from 'flowbite-react';
import { BellIcon, ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { UserMenu } from '../../../shell/components/UserMenu';
import { useMobileShell } from '../../../layout/mobileShell';

interface TopBarProps {
  user?: {
    name?: string;
    email?: string;
    avatar_url?: string;
  };
  tenantName?: string;
  notifications?: number;
  onNotificationClick?: () => void;
  onActivityClick?: () => void;
  /** Passed through to `UserMenu` (e.g. tenant-admin shell) */
  userMenuProfileTo?: string;
  userMenuWorkspaceTo?: string;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  rightContent?: ReactNode;
  /** Rendered immediately before the user avatar (e.g. in-app notification bell). */
  beforeAvatar?: ReactNode;
  /** Rendered immediately after the brand mark (e.g. Traditional ↔ Agentic mode switcher). */
  afterBrand?: ReactNode;
}

export function TopBar({
  user,
  tenantName: _tenantName, // Reserved; logo shown in place of tenant name
  notifications = 0,
  onNotificationClick,
  onActivityClick,
  userMenuProfileTo,
  userMenuWorkspaceTo,
  searchPlaceholder = 'Search...',
  onSearch,
  rightContent,
  beforeAvatar,
  afterBrand,
}: TopBarProps) {
  const { isMobileShell } = useMobileShell();

  return (
    <Navbar
      fluid
      data-shell-app-topbar
      data-mobile-topbar={isMobileShell ? 'true' : 'false'}
      theme={{
        root: {
          base: 'bg-white dark:border-gray-700 dark:bg-gray-800',
          inner: {
            base: 'flex h-full w-full items-center justify-between',
          },
        },
      }}
      className={`relative z-[100] h-14 overflow-visible border-b border-gray-200 bg-white py-0 dark:border-gray-700 dark:bg-gray-800 ${
        isMobileShell ? 'px-2' : 'px-3 sm:px-4'
      }`}
    >
      <div className="flex h-full w-full items-center justify-between gap-2 overflow-visible">
        {/* Left: icon + wordmark + mode switcher — never shrink (toggle must stay visible on resize). */}
        <div
          className="flex shrink-0 items-center gap-2 sm:gap-2.5"
          data-testid="topbar-brand-cluster"
        >
          <Link
            to="/langflow-assistant"
            className="flex h-8 shrink-0 items-center gap-2 rounded-md leading-none outline-none ring-primary-500 focus-visible:ring-2"
            aria-label="Brazn ai home"
          >
            <img
              src="/brazn-logo-header-icon.png"
              alt=""
              className="h-8 w-8 shrink-0 rounded-md object-contain"
              width={32}
              height={32}
            />
            {!isMobileShell ? (
              <span className="hidden whitespace-nowrap text-base font-semibold leading-none tracking-tight text-gray-900 dark:text-white lg:inline">
                Brazn ai
              </span>
            ) : null}
          </Link>
          {afterBrand ? (
            <div className="flex shrink-0 items-center" data-testid="topbar-after-brand">
              {afterBrand}
            </div>
          ) : null}
        </div>

        {/* Right side - Search, Notifications, Profile (may compress; brand/toggle wins). */}
        <div
          className={`flex min-w-0 items-center justify-end ${isMobileShell ? 'gap-1' : 'gap-2 sm:gap-3 lg:gap-4'}`}
        >
          {beforeAvatar}

          {/* Activity Link */}
          {onActivityClick && (
            <button
              onClick={onActivityClick}
              className={`shrink-0 rounded-lg font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 ${
                isMobileShell
                  ? 'flex h-10 w-10 items-center justify-center p-0'
                  : 'flex h-9 w-9 items-center justify-center p-0 xl:h-auto xl:w-auto xl:px-3 xl:py-2 xl:text-sm'
              }`}
              aria-label="Activity"
              title="Activity"
            >
              <ClockIcon className="h-5 w-5 xl:hidden" aria-hidden />
              <span className="hidden xl:inline">Activity</span>
            </button>
          )}

          {/* Search (optional) */}
          {onSearch && (
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-900 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                  placeholder={searchPlaceholder}
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Notifications */}
          {onNotificationClick && (
            <button
              onClick={onNotificationClick}
              className="relative p-2 text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
              aria-label="Notifications"
            >
              <BellIcon className="w-6 h-6" />
              {notifications > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 text-white rounded-full font-semibold">
                  {notifications > 9 ? '9+' : notifications}
                </span>
              )}
            </button>
          )}

          {user && (
            <UserMenu
              user={user}
              variant="topbar"
              profileTo={userMenuProfileTo}
              workspaceTo={userMenuWorkspaceTo}
            />
          )}

          {/* Custom right content */}
          {rightContent}
        </div>
      </div>
    </Navbar>
  );
}
